/**
 * "We couldn't find your IT profile" — the one wording for it, everywhere.
 *
 * Ivanti only knows people HR has synced into it. Someone signed in with a
 * valid Microsoft account can still be missing there (a new starter, a renamed
 * mailbox), and then the portal can neither list their tickets nor raise one.
 * Nothing on the page can fix that, so instead of a retry this says who can:
 * the Global IT Helpdesk, by Teams chat or email.
 *
 * Rendered by `ResultStateNotice` for a list whose `result.reason` is
 * `REQUESTER_UNRESOLVED`, and by `ErrorState` for a write rejected with
 * `REQUESTER_NOT_REGISTERED` — so every list and form that already shows those
 * gets it without knowing it exists.
 */

import { Icon, Mail, MessageSquare } from "@norican/icons";
import { Alert, cn } from "@norican/react";

import { useT } from "@/i18n";
import { HELPDESK_EMAIL, HELPDESK_EMAIL_HREF, HELPDESK_TEAMS_URL } from "@/lib/helpdesk-contact";

const LINK_CLASS =
  "text-accent-fg-subtle hover:text-accent-hover font-weight-medium inline-flex items-center gap-1.5 underline underline-offset-2";

export interface RequesterUnresolvedNoticeProps {
  className?: string;
}

export function RequesterUnresolvedNotice({ className }: RequesterUnresolvedNoticeProps) {
  const { t } = useT();

  return (
    <Alert tone="warning" className={cn(className)}>
      <div className="flex flex-col gap-2">
        <span>{t("requesterUnresolved.body")}</span>
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <a
            href={HELPDESK_TEAMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK_CLASS}
          >
            <Icon icon={MessageSquare} size="sm" />
            {t("requesterUnresolved.teams")}
            <span className="sr-only"> ({t("requesterUnresolved.newTab")})</span>
          </a>
          <a href={HELPDESK_EMAIL_HREF} className={LINK_CLASS}>
            <Icon icon={Mail} size="sm" />
            {HELPDESK_EMAIL}
          </a>
        </span>
      </div>
    </Alert>
  );
}
